import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CAMPAIGN_STATUS_LABEL,
  CHANNEL_KIND_LABEL,
  DELIVERY_MODE_LABEL,
  NOTICE_SEND_POLICY_LABEL,
  PURPOSE_LABEL,
  type CampaignChannelKind,
  type CampaignDeliveryMode,
  type CampaignPurpose,
  type CampaignRecord,
  type CampaignStatus,
  type NoticeProgramRecord,
} from '@workspace/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useConfirm } from '@/contexts/confirmContext';
import {
  CAMPAIGN_NEW_PATH,
  CAMPAIGN_TEMPLATES_PATH,
  NOTICE_PROGRAM_NEW_PATH,
  campaignDetailPath,
  noticeProgramDetailPath,
} from '../api/campaignPaths';
import { contactAgentApi } from '../api/contactAgentApi';
import { contactAgentError } from '../api/contactAgentScope';
import { ContactAgentPage, ContactAgentPageHeader } from '../components/ContactAgentPageHeader';
import { ContactAgentTenantPicker } from '../components/ContactAgentTenantPicker';
import { ContactAgentSetupEventsPanel } from '../components/ContactAgentSetupEventsPanel';
import { useContactAgentTenant } from '../hooks/useContactAgentTenant';

type HubTab = 'campaigns' | 'dispatches';

type HubFilters = {
  deliveryMode: CampaignDeliveryMode | 'ALL';
  channelKind: CampaignChannelKind | 'ALL';
  purpose: CampaignPurpose | 'ALL';
  status: CampaignStatus | 'ALL';
};

const EMPTY_FILTERS: HubFilters = {
  deliveryMode: 'ALL',
  channelKind: 'ALL',
  purpose: 'ALL',
  status: 'ALL',
};

export function CampaignsHubPage() {
  const { confirm } = useConfirm();
  const { tenantId, tenants, isSuperAdmin, ready, selectTenant } = useContactAgentTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: HubTab = searchParams.get('tab') === 'dispatches' ? 'dispatches' : 'campaigns';
  const [campaigns, setCampaigns] = useState<CampaignRecord[]>([]);
  const [programs, setPrograms] = useState<NoticeProgramRecord[]>([]);
  const [filters, setFilters] = useState<HubFilters>(EMPTY_FILTERS);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!ready) {
      return;
    }
    let cancelled = false;
    setError('');
    void Promise.all([contactAgentApi.listCampaigns(), contactAgentApi.listNoticePrograms()])
      .then(([campaignList, programList]) => {
        if (!cancelled) {
          setCampaigns(campaignList);
          setPrograms(programList);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(contactAgentError(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ready, tenantId]);

  const visible = useMemo(() => campaigns.filter(campaign => matchesFilters(campaign, filters)), [campaigns, filters]);
  const emptyList = tab === 'campaigns' && campaigns.length === 0;
  const emptyPrograms = tab === 'dispatches' && programs.length === 0;
  const setTab = (next: HubTab) => {
    if (next === 'dispatches') {
      setSearchParams({ tab: 'dispatches' });
      return;
    }
    setSearchParams({});
  };

  const handleDeleteProgram = async (program: NoticeProgramRecord) => {
    const ok = await confirm({
      title: 'Eliminar disparo',
      message: program.setupOrigin
        ? `Vas a eliminar "${program.name}" y desengancharlo del evento Setup. Esta acción no se puede deshacer.`
        : `Vas a eliminar "${program.name}" permanentemente. Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
    });
    if (!ok) {
      return;
    }
    try {
      await contactAgentApi.deleteNoticeProgram(program.id);
      setPrograms(current => current.filter(item => item.id !== program.id));
    } catch (err) {
      setError(contactAgentError(err));
    }
  };

  return (
    <ContactAgentPage>
      <ContactAgentPageHeader
        eyebrow='Campañas'
        showBack={false}
        title='Campañas'
        description='Lotes de cobranza y aviso. Los disparos de paquetería viven en la otra pestaña.'
        toolbar={
          isSuperAdmin ? (
            <ContactAgentTenantPicker tenants={tenants} tenantId={tenantId} onChange={selectTenant} />
          ) : null
        }
        actions={
          tab === 'dispatches' ? (
            <Button asChild>
              <Link to={NOTICE_PROGRAM_NEW_PATH}>Nuevo disparo</Link>
            </Button>
          ) : (
            <>
              <Button variant='ghost' asChild>
                <Link to={NOTICE_PROGRAM_NEW_PATH}>Nuevo disparo</Link>
              </Button>
              {!emptyList ? (
                <Button asChild>
                  <Link to={CAMPAIGN_NEW_PATH}>Nueva campaña</Link>
                </Button>
              ) : null}
            </>
          )
        }
      />

      <div className='flex flex-wrap gap-2'>
        <Button type='button' size='sm' variant={tab === 'campaigns' ? 'secondary' : 'ghost'} onClick={() => setTab('campaigns')}>
          Campañas
        </Button>
        <Button type='button' size='sm' variant={tab === 'dispatches' ? 'secondary' : 'ghost'} onClick={() => setTab('dispatches')}>
          Disparos
        </Button>
        <Button type='button' size='sm' variant='ghost' asChild>
          <Link to={CAMPAIGN_TEMPLATES_PATH}>Plantillas</Link>
        </Button>
      </div>

      {error ? <p className='text-destructive'>{error}</p> : null}

      {tab === 'dispatches' ? (
        <ContactAgentSetupEventsPanel tenantId={tenantId} programs={programs} />
      ) : null}

      {tab === 'dispatches' && emptyPrograms ? (
        <Card className='rounded-xl border-dashed'>
          <CardContent className='flex flex-col items-center gap-4 py-16 text-center'>
            <p className='text-muted-foreground'>Tu sistema avisa; acá solo se configura el mensaje y el canal.</p>
            <Button asChild>
              <Link to={NOTICE_PROGRAM_NEW_PATH}>Nuevo disparo</Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {tab === 'dispatches' && !emptyPrograms ? (
        <div className='space-y-3'>
          {programs.map(program => (
            <HubProgramRow key={program.id} program={program} onDelete={handleDeleteProgram} />
          ))}
        </div>
      ) : null}

      {tab === 'campaigns' && emptyList ? (
        <Card className='rounded-xl border-dashed'>
          <CardContent className='flex flex-col items-center gap-4 py-16 text-center'>
            <p className='text-muted-foreground'>Todavía no hay campañas.</p>
            <Button asChild>
              <Link to={CAMPAIGN_NEW_PATH}>Nueva campaña</Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {tab === 'campaigns' && !emptyList ? (
        <div className='space-y-4'>
          <HubFilterChips filters={filters} onChange={setFilters} />
          <div className='space-y-3'>
            {visible.map(campaign => (
              <HubCampaignRow key={campaign.id} campaign={campaign} />
            ))}
            {visible.length === 0 ? (
              <p className='text-sm text-muted-foreground'>Ninguna campaña coincide con esos filtros.</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </ContactAgentPage>
  );
}

function HubProgramRow({
  program,
  onDelete,
}: {
  program: NoticeProgramRecord;
  onDelete: (program: NoticeProgramRecord) => Promise<void>;
}) {
  return (
    <div className='flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between'>
      <div className='space-y-2'>
        <Link to={noticeProgramDetailPath(program.id)} className='font-medium hover:underline'>
          {program.name}
        </Link>
        <div className='flex flex-wrap gap-2'>
          <Badge variant='secondary'>{PURPOSE_LABEL[program.purpose]}</Badge>
          <Badge variant='outline'>{CHANNEL_KIND_LABEL[program.channelKind]}</Badge>
          <Badge variant='outline'>{NOTICE_SEND_POLICY_LABEL[program.sendPolicy]}</Badge>
          <Badge>{CAMPAIGN_STATUS_LABEL[program.status]}</Badge>
        </div>
      </div>
      <div className='flex flex-wrap gap-2'>
        <Button variant='outline' size='sm' asChild>
          <Link to={noticeProgramDetailPath(program.id)}>Ver</Link>
        </Button>
        <Button variant='destructive' size='sm' onClick={() => void onDelete(program)}>
          Eliminar
        </Button>
      </div>
    </div>
  );
}

function HubFilterChips({
  filters,
  onChange,
}: {
  filters: HubFilters;
  onChange: (next: HubFilters) => void;
}) {
  return (
    <div className='flex flex-wrap gap-4'>
      <ChipGroup
        legend='Modo'
        value={filters.deliveryMode}
        options={[
          { id: 'ALL', label: 'Todos' },
          { id: 'CONVERSATIONAL', label: DELIVERY_MODE_LABEL.CONVERSATIONAL },
          { id: 'NOTIFICATION', label: DELIVERY_MODE_LABEL.NOTIFICATION },
        ]}
        onChange={id => onChange({ ...filters, deliveryMode: id as HubFilters['deliveryMode'] })}
      />
      <ChipGroup
        legend='Canal'
        value={filters.channelKind}
        options={[
          { id: 'ALL', label: 'Todos' },
          { id: 'WHATSAPP', label: CHANNEL_KIND_LABEL.WHATSAPP },
          { id: 'SMS', label: CHANNEL_KIND_LABEL.SMS },
          { id: 'EMAIL', label: CHANNEL_KIND_LABEL.EMAIL },
        ]}
        onChange={id => onChange({ ...filters, channelKind: id as HubFilters['channelKind'] })}
      />
      <ChipGroup
        legend='Propósito'
        value={filters.purpose}
        options={[
          { id: 'ALL', label: 'Todos' },
          { id: 'COBRANZAS', label: PURPOSE_LABEL.COBRANZAS },
          { id: 'AVISO', label: PURPOSE_LABEL.AVISO },
        ]}
        onChange={id => onChange({ ...filters, purpose: id as HubFilters['purpose'] })}
      />
      <ChipGroup
        legend='Estado'
        value={filters.status}
        options={[
          { id: 'ALL', label: 'Todos' },
          { id: 'DRAFT', label: CAMPAIGN_STATUS_LABEL.DRAFT },
          { id: 'ACTIVE', label: CAMPAIGN_STATUS_LABEL.ACTIVE },
          { id: 'PAUSED', label: CAMPAIGN_STATUS_LABEL.PAUSED },
          { id: 'COMPLETED', label: CAMPAIGN_STATUS_LABEL.COMPLETED },
        ]}
        onChange={id => onChange({ ...filters, status: id as HubFilters['status'] })}
      />
    </div>
  );
}

function ChipGroup({
  legend,
  value,
  options,
  onChange,
}: {
  legend: string;
  value: string;
  options: Array<{ id: string; label: string }>;
  onChange: (id: string) => void;
}) {
  return (
    <fieldset className='space-y-2'>
      <legend className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>{legend}</legend>
      <div className='flex flex-wrap gap-2'>
        {options.map(option => (
          <Button
            key={option.id}
            type='button'
            size='sm'
            variant={value === option.id ? 'secondary' : 'outline'}
            onClick={() => onChange(option.id)}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </fieldset>
  );
}

function HubCampaignRow({ campaign }: { campaign: CampaignRecord }) {
  return (
    <div className='flex flex-col gap-3 rounded-xl border px-4 py-3 sm:flex-row sm:items-center sm:justify-between'>
      <div className='space-y-2'>
        <Link to={campaignDetailPath(campaign.id)} className='font-medium hover:underline'>
          {campaign.name}
        </Link>
        <div className='flex flex-wrap gap-2'>
          <Badge variant='secondary'>{PURPOSE_LABEL[campaign.purpose]}</Badge>
          <Badge variant='outline'>{DELIVERY_MODE_LABEL[campaign.deliveryMode]}</Badge>
          <Badge variant='outline'>{CHANNEL_KIND_LABEL[campaign.channelKind]}</Badge>
          <Badge>{CAMPAIGN_STATUS_LABEL[campaign.status]}</Badge>
        </div>
      </div>
      <Button variant='outline' size='sm' asChild>
        <Link to={campaignDetailPath(campaign.id)}>Ver</Link>
      </Button>
    </div>
  );
}

function matchesFilters(campaign: CampaignRecord, filters: HubFilters): boolean {
  if (filters.deliveryMode !== 'ALL' && campaign.deliveryMode !== filters.deliveryMode) {
    return false;
  }
  if (filters.channelKind !== 'ALL' && campaign.channelKind !== filters.channelKind) {
    return false;
  }
  if (filters.purpose !== 'ALL' && campaign.purpose !== filters.purpose) {
    return false;
  }
  if (filters.status !== 'ALL' && campaign.status !== filters.status) {
    return false;
  }
  return true;
}
